import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
  Paper,
  Collapse,
  Alert,
  Checkbox,
  FormControlLabel,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  InfoOutlined as InfoIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
  CloudUpload as CloudUploadIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  useUpdateCaseMutation,
  useGetCaseAttachmentsQuery,
  useGetUploadUrlMutation,
  useConfirmUploadMutation,
  useDeleteCaseAttachmentMutation,
} from '../../../store/api/apiSlice';
import type {
  DynamicCase,
  MedicalHistory,
  PatientDateOfBirth,
  PatientInformation,
  PrescriptionEntry,
  ScoringRubricItem,
  UpdateCaseRequest
} from '../../../types';

interface EditCaseModalProps {
  open: boolean;
  onClose: () => void;
  case: DynamicCase;
  onCaseUpdated: (updatedCase: DynamicCase) => void;
  onError: (error: string) => void;
}

// Form state interface (using strings for form inputs)
interface EditCaseFormData {
  title?: string;
  preview?: string;
  author?: string;
  peer_reviewer?: string;
  difficulty_level?: "beginner" | "intermediate" | "advanced";
  age?: string; // String for TextField input
  gender?: string;
  occupation?: string;
  personality?: string;
  primary_condition?: string;
  prompt_instructions?: string;
  scoring_logic?: string;
  activity_idle_timeout?: string; // String for TextField input
}

interface EditCaseSnapshot {
  formData: EditCaseFormData;
  patientInformation: PatientInformation;
  prescriptions: PrescriptionEntry[];
  medicalHistory: MedicalHistory;
  scoringItems: ScoringRubricItem[];
  reviewConfirmed: boolean;
}

const createEmptyPatientInformation = (): PatientInformation => ({
  full_name: '',
  date_of_birth: {
    day: null,
    month: null,
    year: null,
  },
  street_address: '',
  city: '',
  province_state: '',
  postal_zip_code: '',
  health_card: '',
});

const parseInteger = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }

  return null;
};

const parseDateOfBirthParts = (value: unknown): PatientDateOfBirth => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const source = value as Partial<Record<keyof PatientDateOfBirth, unknown>>;
    return {
      day: parseInteger(source.day),
      month: parseInteger(source.month),
      year: parseInteger(source.year),
    };
  }

  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) {
    return { day: null, month: null, year: null };
  }

  const parsedDate = new Date(text);
  if (!Number.isNaN(parsedDate.getTime())) {
    return {
      day: parsedDate.getDate(),
      month: parsedDate.getMonth() + 1,
      year: parsedDate.getFullYear(),
    };
  }

  const numericMatch = text.match(/\b(\d{1,2})[/-](\d{1,2})[/-](\d{4})\b/);
  if (!numericMatch) {
    return { day: null, month: null, year: null };
  }

  const first = Number(numericMatch[1]);
  const second = Number(numericMatch[2]);
  return {
    day: first > 12 ? first : second,
    month: first > 12 ? second : first,
    year: Number(numericMatch[3]),
  };
};

const normalizePatientInformationForForm = (value: PatientInformation | null): PatientInformation => {
  if (!value || typeof value !== 'object') {
    return createEmptyPatientInformation();
  }

  const source = value as PatientInformation & { address?: string };
  const legacyAddress = typeof source.address === 'string' ? source.address.trim() : '';

  return {
    full_name: source.full_name || '',
    date_of_birth: parseDateOfBirthParts(source.date_of_birth),
    street_address: source.street_address || legacyAddress,
    city: source.city || '',
    province_state: source.province_state || '',
    postal_zip_code: source.postal_zip_code || '',
    health_card: source.health_card || '',
  };
};

const formatDateOfBirthInput = (
  value: number | null,
  digits: 2 | 4
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return digits === 2 ? value.toString().padStart(2, '0') : value.toString();
};

const isValidDateParts = (day: number, month: number, year: number): boolean => {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const createEmptyPrescription = (): PrescriptionEntry => ({
  name: '',
  category: 'prescription',
  dose: '',
  patient_name: '',
  written_date: '',
  directions: '',
  quantity: '',
  refills: '',
  prescriber: '',
  signature: '',
});

const createEmptyMedicalHistory = (): MedicalHistory => ({
  past_medical_history: [],
  past_surgeries: [],
  allergies: [],
  social_history: [],
});

const prescriptionHasContent = (entry: PrescriptionEntry): boolean =>
  [
    entry.name,
    entry.dose,
    entry.patient_name,
    entry.written_date,
    entry.directions,
    entry.quantity,
    entry.refills,
    entry.prescriber,
    entry.signature,
  ].some((value) => value.trim() !== '');

const normalizePrescriptionEntry = (entry?: Partial<PrescriptionEntry> | null): PrescriptionEntry => ({
  ...createEmptyPrescription(),
  ...(entry || {}),
});

const normalizePrescriptionList = (value: unknown): PrescriptionEntry[] => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizePrescriptionEntry(entry));
  }

  if (value && typeof value === 'object') {
    return [normalizePrescriptionEntry(value as Partial<PrescriptionEntry>)].filter(prescriptionHasContent);
  }

  return [];
};

const grammarCheckInputProps = {
  'data-gramm': 'false',
  'data-gramm_editor': 'false',
  'data-enable-grammarly': 'false',
};

const getCaseHydrationKey = (caseItem?: DynamicCase | null): string | null => {
  if (!caseItem) {
    return null;
  }

  return `${caseItem.id}:${String(caseItem.updated_at || '')}`;
};

const parseScoringItems = (scoringLogic: string | null): ScoringRubricItem[] => {
  if (!scoringLogic) {
    return [];
  }

  try {
    const parsed = JSON.parse(scoringLogic) as ScoringRubricItem[];
    return parsed.map(item => ({
      ...item,
      messages: item.messages || [],
      categorySpecificInstructions: item.categorySpecificInstructions || "",
      evaluationTiming: item.evaluationTiming || "per-exchange"
    }));
  } catch (e) {
    console.error("Invalid scoring_logic JSON", e);
    return [];
  }
};

const serializeEditCaseSnapshot = (snapshot: EditCaseSnapshot): string => JSON.stringify(snapshot);

export const EditCaseModal: React.FC<EditCaseModalProps> = ({
  open,
  onClose,
  case: caseItem,
  onCaseUpdated,
  onError
}) => {
  const [formData, setFormData] = useState<EditCaseFormData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scoringErrors, setScoringErrors] = useState<Record<number, Record<string, string>>>({});
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [hydratedCaseKey, setHydratedCaseKey] = useState<string | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [isUnsavedWarningOpen, setIsUnsavedWarningOpen] = useState(false);
  const [patientInformation, setPatientInformation] = useState<PatientInformation>(createEmptyPatientInformation());
  const [prescriptions, setPrescriptions] = useState<PrescriptionEntry[]>([]);
  const normalizedPrescriptions = normalizePrescriptionList(prescriptions);
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>(createEmptyMedicalHistory());

  const [updateCase, { isLoading }] = useUpdateCaseMutation();

  // Attachment state
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: attachments = [], isLoading: isLoadingAttachments } = useGetCaseAttachmentsQuery(
    caseItem?.id ?? '',
    { skip: !open || !caseItem?.id }
  );
  const [getUploadUrl] = useGetUploadUrlMutation();
  const [confirmUpload] = useConfirmUploadMutation();
  const [deleteAttachment] = useDeleteCaseAttachmentMutation();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !caseItem) return;

    // Reset input so the same file can be re-selected
    e.target.value = '';

    try {
      setUploadProgress(`Preparing upload for ${file.name}...`);

      // 1. Get presigned upload URL
      const { uploadUrl, s3Key } = await getUploadUrl({
        caseId: caseItem.id,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      }).unwrap();

      // 2. Upload directly to S3
      setUploadProgress(`Uploading ${file.name}...`);
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload to S3 failed');
      }

      // 3. Confirm upload with backend
      setUploadProgress('Saving...');
      await confirmUpload({
        caseId: caseItem.id,
        s3Key,
        fileName: s3Key.split('/').pop() || file.name,
        originalName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      }).unwrap();

      setUploadProgress(null);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setUploadProgress(null);
      onError(err?.data?.message || err?.message || 'Failed to upload file');
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!caseItem) return;
    try {
      await deleteAttachment({ caseId: caseItem.id, attachmentId }).unwrap();
    } catch (err: any) {
      console.error('Delete failed:', err);
      onError(err?.data?.message || 'Failed to delete attachment');
    }
  };

  const getAttachmentIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
    if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <DocIcon color="info" />;
    return <FileIcon />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const [scoringItems, setScoringItems] = useState<ScoringRubricItem[]>([]);
  const [scoringExpanded, setScoringExpanded] = useState(true);
  const caseHydrationKey = getCaseHydrationKey(caseItem);
  const isInitializingForm = open && (!caseHydrationKey || hydratedCaseKey !== caseHydrationKey);

  // Initialize form data when modal opens or case changes
  useEffect(() => {
    if (!open) {
      setHydratedCaseKey(null);
      setInitialSnapshot(null);
      setIsUnsavedWarningOpen(false);
      return;
    }

    if (!caseItem || !caseHydrationKey) {
      return;
    }

    const nextFormData: EditCaseFormData = {
      title: caseItem.title || '',
      preview: caseItem.preview || '',
      author: caseItem.author || '',
      peer_reviewer: caseItem.peer_reviewer || '',
      difficulty_level: caseItem.difficulty_level || 'beginner',
      age: caseItem.age ? caseItem.age.toString() : '', // Convert number to string
      gender: caseItem.gender || '',
      occupation: caseItem.occupation || '',
      personality: caseItem.personality || '',
      primary_condition: caseItem.primary_condition || '',
      prompt_instructions: caseItem.prompt_instructions || '',
      scoring_logic: caseItem.scoring_logic || '',
      activity_idle_timeout: (caseItem.activity_idle_timeout ?? 2).toString(),
    };
    const nextPatientInformation = normalizePatientInformationForForm(caseItem.patient_information);
    const nextPrescriptions = normalizePrescriptionList(caseItem.prescriptions);
    const nextMedicalHistory = caseItem.medical_history || createEmptyMedicalHistory();
    const nextScoringItems = parseScoringItems(caseItem.scoring_logic);

    setFormData(nextFormData);
    setPatientInformation(nextPatientInformation);
    setPrescriptions(nextPrescriptions);
    setMedicalHistory(nextMedicalHistory);
    setScoringItems(nextScoringItems);
    setErrors({});
    setScoringErrors({});
    setReviewConfirmed(false);
    setIsUnsavedWarningOpen(false);
    setInitialSnapshot(serializeEditCaseSnapshot({
      formData: nextFormData,
      patientInformation: nextPatientInformation,
      prescriptions: nextPrescriptions,
      medicalHistory: nextMedicalHistory,
      scoringItems: nextScoringItems,
      reviewConfirmed: false,
    }));

    const frameId = window.requestAnimationFrame(() => {
      setHydratedCaseKey(caseHydrationKey);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [open, caseItem, caseHydrationKey]);

  const validateScoringItem = (item: ScoringRubricItem): Record<string, string> => {
    const itemErrors: Record<string, string> = {};

    if (!item.scoringCategory || item.scoringCategory.trim() === '') {
      itemErrors.scoringCategory = 'Category name is required';
    } else if (item.scoringCategory.length > 200) {
      itemErrors.scoringCategory = 'Category name cannot exceed 200 characters';
    }

    if (item.totalPoints < 1) {
      itemErrors.totalPoints = 'Total points must be at least 1';
    } else if (item.totalPoints > 100) {
      itemErrors.totalPoints = 'Total points cannot exceed 100';
    }

    if (item.categorySpecificInstructions && item.categorySpecificInstructions.length > 1000) {
      itemErrors.categorySpecificInstructions = 'Instructions cannot exceed 1000 characters';
    }

    return itemErrors;
  };

  const handleScoringItemChange = (index: number, field: keyof ScoringRubricItem, value: string | number) => {
    const updated = [...scoringItems];
    
    // Ensure correct type
    if (field === 'pointsScored' || field === 'totalPoints') {
      updated[index][field] = Number(value);
    } else if (field === 'messages') {
      // Messages should not be editable in this modal
      return;
    } else if (field === 'evaluationTiming') {
      updated[index][field] = value as "per-exchange" | "end-of-session";
    } else {
      updated[index][field] = value as string;
    }
    
    setScoringItems(updated);

    // Validate this specific item
      const itemErrors = validateScoringItem(updated[index]);
    setScoringErrors(prev => ({
      ...prev,
      [index]: itemErrors
    }));

    // Reflect back to formData as JSON string
    setFormData(prev => ({
      ...prev,
      scoring_logic: JSON.stringify(updated)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.title && formData.title.length > 200) {
      newErrors.title = 'Title cannot exceed 200 characters';
    }

    if (formData.preview && formData.preview.length > 500) {
      newErrors.preview = 'Preview cannot exceed 500 characters';
    }

    if (formData.age && formData.age.trim() !== '') {
      const ageNum = Number(formData.age);
      if (isNaN(ageNum) || ageNum < 0 || ageNum > 150) {
        newErrors.age = 'Age must be a valid number between 0 and 150';
      }
    }

    if (formData.occupation && formData.occupation.length > 100) {
      newErrors.occupation = 'Occupation cannot exceed 100 characters';
    }

    if (formData.personality && formData.personality.length > 1000) {
      newErrors.personality = 'Personality description cannot exceed 1000 characters';
    }

    if (formData.primary_condition && formData.primary_condition.length > 200) {
      newErrors.primary_condition = 'Primary condition cannot exceed 200 characters';
    }

    if (formData.prompt_instructions && formData.prompt_instructions.length > 2000) {
      newErrors.prompt_instructions = 'Prompt instructions cannot exceed 2000 characters';
    }

    if (formData.activity_idle_timeout !== undefined && formData.activity_idle_timeout !== '') {
      const timeout = Number(formData.activity_idle_timeout);
      if (!Number.isInteger(timeout) || timeout < 1 || timeout > 10) {
        newErrors.activity_idle_timeout = 'Idle timeout must be a whole number between 1 and 10 minutes';
      }
    }

    if (patientInformation.full_name.length > 200) {
      newErrors.patient_information_full_name = 'Full name cannot exceed 200 characters';
    }

    const { day, month, year } = patientInformation.date_of_birth;
    const hasAnyDateOfBirthPart = [day, month, year].some((part) => part !== null && part !== undefined);

    if (hasAnyDateOfBirthPart) {
      if (!Number.isInteger(day) || day === null || day < 1 || day > 31) {
        newErrors.patient_information_date_of_birth_day = 'Day must be a 2-digit value from 01 to 31';
      }

      if (!Number.isInteger(month) || month === null || month < 1 || month > 12) {
        newErrors.patient_information_date_of_birth_month = 'Month must be a 2-digit value from 01 to 12';
      }

      if (!Number.isInteger(year) || year === null || year < 1000 || year > 9999) {
        newErrors.patient_information_date_of_birth_year = 'Year must be a 4-digit value';
      }

      if (
        Number.isInteger(day) &&
        Number.isInteger(month) &&
        Number.isInteger(year) &&
        day !== null &&
        month !== null &&
        year !== null &&
        !isValidDateParts(day, month, year)
      ) {
        newErrors.patient_information_date_of_birth_day = 'Date of birth must be a valid calendar date';
      }
    }

    if (patientInformation.street_address.length > 300) {
      newErrors.patient_information_street_address = 'Street address cannot exceed 300 characters';
    }

    if (patientInformation.city.length > 100) {
      newErrors.patient_information_city = 'City cannot exceed 100 characters';
    }

    if (patientInformation.province_state.length > 100) {
      newErrors.patient_information_province_state = 'Province/state cannot exceed 100 characters';
    }

    if (patientInformation.postal_zip_code.length > 30) {
      newErrors.patient_information_postal_zip_code = 'Postal/ZIP code cannot exceed 30 characters';
    }

    if (patientInformation.health_card.length > 100) {
      newErrors.patient_information_health_card = 'Health card cannot exceed 100 characters';
    }

    normalizedPrescriptions.forEach((entry, index) => {
      const hasAnyValue = prescriptionHasContent(entry);

      if (hasAnyValue && entry.name.trim() === '') {
        newErrors[`prescription_${index}_name`] = 'Medication name is required when a prescription row is used';
      }

      if (entry.name.length > 200) {
        newErrors[`prescription_${index}_name`] = 'Medication name cannot exceed 200 characters';
      }

      if (entry.patient_name.length > 200) {
        newErrors[`prescription_${index}_patient_name`] = 'Patient name cannot exceed 200 characters';
      }

      if (entry.written_date.length > 100) {
        newErrors[`prescription_${index}_written_date`] = 'Written date cannot exceed 100 characters';
      }

      if (entry.directions.length > 1000) {
        newErrors[`prescription_${index}_directions`] = 'Directions cannot exceed 1000 characters';
      }

      if (entry.quantity.length > 100) {
        newErrors[`prescription_${index}_quantity`] = 'Quantity cannot exceed 100 characters';
      }

      if (entry.refills.length > 100) {
        newErrors[`prescription_${index}_refills`] = 'Refills cannot exceed 100 characters';
      }

      if (entry.prescriber.length > 200) {
        newErrors[`prescription_${index}_prescriber`] = 'Prescriber cannot exceed 200 characters';
      }

      if (entry.signature.length > 500) {
        newErrors[`prescription_${index}_signature`] = 'Signature cannot exceed 500 characters';
      }
    });

    const medicalHistoryFieldLimits: Array<[keyof MedicalHistory, number, string]> = [
      ['past_medical_history', 300, 'Past medical history items'],
      ['past_surgeries', 300, 'Past surgery items'],
      ['allergies', 300, 'Allergy items'],
      ['social_history', 500, 'Social history items'],
    ];

    medicalHistoryFieldLimits.forEach(([field, limit, label]) => {
      medicalHistory[field].forEach((item, index) => {
        if (item.length > limit) {
          newErrors[`medical_history_${field}_${index}`] = `${label} cannot exceed ${limit} characters`;
        }
      });
    });

    // Validate all scoring items
    const newScoringErrors: Record<number, Record<string, string>> = {};
    let hasScoringErrors = false;

    scoringItems.forEach((item, index) => {
      const itemErrors = validateScoringItem(item);
      if (Object.keys(itemErrors).length > 0) {
        newScoringErrors[index] = itemErrors;
        hasScoringErrors = true;
      }
    });

    setErrors(newErrors);
    setScoringErrors(newScoringErrors);

    return Object.keys(newErrors).length === 0 && !hasScoringErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Filter out empty values and convert types appropriately
      const updateData: UpdateCaseRequest = {};
      
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          if (key === 'age') {
            const ageNum = Number(value);
            if (!isNaN(ageNum)) {
              updateData.age = ageNum;
            }
          } else if (key === 'activity_idle_timeout') {
            const timeout = Number(value);
            if (!isNaN(timeout)) {
              updateData.activity_idle_timeout = timeout;
            }
          } else {
            (updateData as Record<string, unknown>)[key] = value;
          }
        }
      });

      updateData.patient_information = patientInformation;
      updateData.prescriptions = normalizedPrescriptions.filter((entry) => prescriptionHasContent(entry));
      updateData.medical_history = {
        past_medical_history: medicalHistory.past_medical_history.filter((item) => item.trim() !== ''),
        past_surgeries: medicalHistory.past_surgeries.filter((item) => item.trim() !== ''),
        allergies: medicalHistory.allergies.filter((item) => item.trim() !== ''),
        social_history: medicalHistory.social_history.filter((item) => item.trim() !== ''),
      };

      if (reviewConfirmed) {
        updateData.processing_status = 'completed';
      }

      const result = await updateCase({
        caseId: caseItem.id,
        updates: updateData
      }).unwrap();
      
      onCaseUpdated(result);
      onClose();
    } catch (error: any) {
      console.error('Failed to update case:', error);
      onError(error.message || 'Failed to update case');
    }
  };

  const handleChange = (field: keyof EditCaseFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePatientInformationChange = (field: keyof PatientInformation) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setPatientInformation((prev) => ({
      ...prev,
      [field]: value,
    }));

    const errorKey = `patient_information_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }));
    }
  };

  const handlePatientDateOfBirthChange = (
    field: keyof PatientDateOfBirth,
    maxDigits: 2 | 4
  ) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const rawDigits = e.target.value.replace(/\D/g, '');
    const digitsOnly = maxDigits === 2 ? rawDigits.slice(-2) : rawDigits.slice(0, maxDigits);
    const value = digitsOnly === '' || (maxDigits === 2 && digitsOnly === '0') ? null : Number(digitsOnly);

    setPatientInformation((prev) => ({
      ...prev,
      date_of_birth: {
        ...prev.date_of_birth,
        [field]: value,
      },
    }));

    const fieldErrorKey = `patient_information_date_of_birth_${field}`;
    if (errors[fieldErrorKey]) {
      setErrors((prev) => ({
        ...prev,
        [fieldErrorKey]: '',
      }));
    }
  };

  const handlePrescriptionChange = (
    index: number,
    field: keyof PrescriptionEntry,
    value: string
  ) => {
    setPrescriptions((prev) =>
      normalizePrescriptionList(prev).map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      )
    );

    const errorKey = `prescription_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }));
    }
  };

  const handleAddPrescription = () => {
    setPrescriptions((prev) => [...normalizePrescriptionList(prev), createEmptyPrescription()]);
  };

  const handleRemovePrescription = (index: number) => {
    setPrescriptions((prev) => normalizePrescriptionList(prev).filter((_, entryIndex) => entryIndex !== index));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`prescription_${index}_`)) {
          delete next[key];
        }
      });
      return next;
    });
  };

  const handleMedicalHistoryItemChange = (
    field: keyof MedicalHistory,
    index: number,
    value: string
  ) => {
    setMedicalHistory((prev) => ({
      ...prev,
      [field]: prev[field].map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));

    const errorKey = `medical_history_${field}_${index}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }));
    }
  };

  const handleAddMedicalHistoryItem = (field: keyof MedicalHistory) => {
    setMedicalHistory((prev) => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const handleRemoveMedicalHistoryItem = (field: keyof MedicalHistory, index: number) => {
    setMedicalHistory((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, itemIndex) => itemIndex !== index),
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`medical_history_${field}_${index}`];
      return next;
    });
  };

  const handleAddScoringItem = () => {
    const newItem: ScoringRubricItem = {
      scoringCategory: '',
      totalPoints: 1,
      pointsScored: 0,
      evaluationNotes: "",
      messages: [],
      categorySpecificInstructions: "",
      evaluationTiming: "per-exchange"
    };
    
    const updated = [...scoringItems, newItem];
    setScoringItems(updated);
    setFormData(prev => ({
      ...prev,
      scoring_logic: JSON.stringify(updated),
    }));
  };

  const handleMoveScoringItem = (fromIndex: number, toIndex: number) => {
    const updated = [...scoringItems];
    [updated[fromIndex], updated[toIndex]] = [updated[toIndex], updated[fromIndex]];
    setScoringItems(updated);
    setFormData(prev => ({
      ...prev,
      scoring_logic: JSON.stringify(updated),
    }));

    // Keep validation errors in sync with moved items
    setScoringErrors(prev => {
      const next = { ...prev };
      const fromErrors = next[fromIndex];
      const toErrors = next[toIndex];
      if (toErrors) next[fromIndex] = toErrors; else delete next[fromIndex];
      if (fromErrors) next[toIndex] = fromErrors; else delete next[toIndex];
      return next;
    });
  };

  const handleRemoveScoringItem = (index: number) => {
    const updated = scoringItems.filter((_, i) => i !== index);
    setScoringItems(updated);
    setFormData(prev => ({
      ...prev,
      scoring_logic: JSON.stringify(updated),
    }));

    // Clear errors for this item
    setScoringErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  const currentSnapshot = useMemo(
    () => serializeEditCaseSnapshot({
      formData,
      patientInformation,
      prescriptions: normalizedPrescriptions,
      medicalHistory,
      scoringItems,
      reviewConfirmed,
    }),
    [formData, patientInformation, normalizedPrescriptions, medicalHistory, scoringItems, reviewConfirmed]
  );
  const hasUnsavedChanges = open && !isInitializingForm && initialSnapshot !== null && currentSnapshot !== initialSnapshot;

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    if (hasUnsavedChanges) {
      setIsUnsavedWarningOpen(true);
      return;
    }

    onClose();
  };

  const handleKeepEditing = () => {
    setIsUnsavedWarningOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsUnsavedWarningOpen(false);
    onClose();
  };

  const hasAnyScoringErrors = Object.values(scoringErrors).some(itemErrors => 
    Object.keys(itemErrors).length > 0
  );
  const medicalHistorySections: Array<{ key: keyof MedicalHistory; label: string; helper: string }> = [
    { key: 'past_medical_history', label: 'Past Medical History', helper: 'Add one condition or history item per row.' },
    { key: 'past_surgeries', label: 'Past Surgeries', helper: 'Add one surgery or procedure per row.' },
    { key: 'allergies', label: 'Allergies', helper: 'Add one allergy or adverse reaction per row.' },
    { key: 'social_history', label: 'Social History', helper: 'Add one social history fact per row.' },
  ];

  if (!caseItem) return null;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Edit Case
        </Typography>
        <IconButton 
          onClick={handleClose} 
          disabled={isLoading}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {isInitializingForm ? (
        <DialogContent
          sx={{
            minHeight: 360,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={36} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading case details...
          </Typography>
        </DialogContent>
      ) : (
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Update the case details. Only fields that are changed will be updated.
          </Typography>

          {caseItem.processing_status === 'review_required' && (
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(139, 92, 246, 0.12)'
                    : 'rgba(139, 92, 246, 0.07)',
                border: '1px solid rgba(139, 92, 246, 0.35)',
                color: 'text.primary',
                '& .MuiAlert-icon': { color: '#8b5cf6' },
              }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>This case requires manual review.</strong> It was flagged during AI processing because the extracted data may be incomplete or inaccurate. Please review the case details and scoring rubric below and make any necessary corrections.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reviewConfirmed}
                    onChange={(e) => setReviewConfirmed(e.target.checked)}
                    disabled={isLoading}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    I have reviewed this case and confirmed it is complete and ready to use.
                  </Typography>
                }
              />
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Basic Information
              </Typography>
              <Tooltip
                title="This information is shown to students for display and reference purposes. It does not affect how the AI Patient behaves."
                placement="top"
                arrow
              >
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', mb: 2 }} />
              </Tooltip>
            </Box>

            <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.title}>
              <InputLabel shrink>Case Title</InputLabel>
              <OutlinedInput
                label="Case Title"
                value={formData.title || ''}
                onChange={handleChange('title')}
                notched
              />
              {errors.title && <FormHelperText>{errors.title}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.preview}>
              <InputLabel shrink>Case Preview</InputLabel>
              <OutlinedInput
                label="Case Preview"
                multiline
                rows={3}
                inputProps={grammarCheckInputProps}
                value={formData.preview || ''}
                onChange={handleChange('preview')}
                notched
              />
              {errors.preview && <FormHelperText>{errors.preview}</FormHelperText>}
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }} variant="outlined" disabled={isLoading}>
                <InputLabel shrink>Author</InputLabel>
                <OutlinedInput
                  label="Author"
                  value={formData.author || ''}
                  onChange={handleChange('author')}
                  notched
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }} variant="outlined" disabled={isLoading}>
                <InputLabel  shrink>Peer Reviewer</InputLabel>
                <OutlinedInput
                  notched
                  label="Peer Reviewer"
                  value={formData.peer_reviewer || ''}
                  onChange={handleChange('peer_reviewer')}
                />
              </FormControl>
            </Box>

            {/* Patient Demographics */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Patient Demographics
              </Typography>
              <Tooltip
                title="This information is shown to students for reference and may subtly influence the AI Patient's responses."
                placement="top"
                arrow
              >
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', mb: 2 }} />
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }} variant="outlined" disabled={isLoading} error={!!errors.age}>
                <InputLabel shrink>Age</InputLabel>
                <OutlinedInput
                  type="number"
                  value={formData.age || ''}
                  onChange={(e) => handleChange('age')(e)}
                  inputProps={{ min: 0, max: 150 }}
                  label="Age"
                  notched
              />
                {errors.age && <FormHelperText>{errors.age}</FormHelperText>}
              </FormControl>

              <FormControl sx={{ flex: 1 }} disabled={isLoading}>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender || ''}
                  label="Gender"
                  onChange={handleChange('gender')}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="non-binary">Non-binary</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1 }} disabled={isLoading}>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficulty_level || 'beginner'}
                  label="Difficulty Level"
                  onChange={handleChange('difficulty_level')}
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.occupation}>
              <InputLabel shrink>Occupation</InputLabel>
              <OutlinedInput
                label="Occupation"
                value={formData.occupation || ''}
                onChange={handleChange('occupation')}
                notched
              />
              {errors.occupation && <FormHelperText>{errors.occupation}</FormHelperText>}
            </FormControl>

            {/* Session Overlay Structured Data */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Session Overlay Data
              </Typography>
              <Tooltip
                title="These fields populate the new diagnostic-session overlays for patient information, prescriptions, and medical history."
                placement="top"
                arrow
              >
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', mb: 2 }} />
              </Tooltip>
            </Box>

            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Patient Information
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.patient_information_full_name}>
                  <InputLabel shrink>Full Name</InputLabel>
                  <OutlinedInput
                    label="Full Name"
                    value={patientInformation.full_name}
                    onChange={handlePatientInformationChange('full_name')}
                    notched
                  />
                  {errors.patient_information_full_name && <FormHelperText>{errors.patient_information_full_name}</FormHelperText>}
                </FormControl>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, mb: 0.75 }}>
                      Date of Birth
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                      <FormControl variant="outlined" disabled={isLoading} error={!!errors.patient_information_date_of_birth_day}>
                        <InputLabel shrink>Day</InputLabel>
                        <OutlinedInput
                          label="Day"
                          value={formatDateOfBirthInput(patientInformation.date_of_birth.day, 2)}
                          onChange={handlePatientDateOfBirthChange('day', 2)}
                          inputProps={{ inputMode: 'numeric', maxLength: 2, pattern: '[0-9]*' }}
                          notched
                        />
                        {errors.patient_information_date_of_birth_day && <FormHelperText>{errors.patient_information_date_of_birth_day}</FormHelperText>}
                      </FormControl>

                      <FormControl variant="outlined" disabled={isLoading} error={!!errors.patient_information_date_of_birth_month}>
                        <InputLabel shrink>Month</InputLabel>
                        <OutlinedInput
                          label="Month"
                          value={formatDateOfBirthInput(patientInformation.date_of_birth.month, 2)}
                          onChange={handlePatientDateOfBirthChange('month', 2)}
                          inputProps={{ inputMode: 'numeric', maxLength: 2, pattern: '[0-9]*' }}
                          notched
                        />
                        {errors.patient_information_date_of_birth_month && <FormHelperText>{errors.patient_information_date_of_birth_month}</FormHelperText>}
                      </FormControl>

                      <FormControl variant="outlined" disabled={isLoading} error={!!errors.patient_information_date_of_birth_year}>
                        <InputLabel shrink>Year</InputLabel>
                        <OutlinedInput
                          label="Year"
                          value={formatDateOfBirthInput(patientInformation.date_of_birth.year, 4)}
                          onChange={handlePatientDateOfBirthChange('year', 4)}
                          inputProps={{ inputMode: 'numeric', maxLength: 4, pattern: '[0-9]*' }}
                          notched
                        />
                        {errors.patient_information_date_of_birth_year && <FormHelperText>{errors.patient_information_date_of_birth_year}</FormHelperText>}
                      </FormControl>
                    </Box>
                  </Box>

                  <FormControl sx={{ marginTop: 2.8 }} fullWidth variant="outlined" disabled={isLoading} error={!!errors.patient_information_health_card}>
                    <InputLabel shrink>Health Card / Identifier</InputLabel>
                    <OutlinedInput
                      label="Health Card / Identifier"
                      value={patientInformation.health_card}
                      onChange={handlePatientInformationChange('health_card')}
                      notched
                    />
                    {errors.patient_information_health_card && <FormHelperText>{errors.patient_information_health_card}</FormHelperText>}
                  </FormControl>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2 }}>
                  <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.patient_information_street_address}>
                    <InputLabel shrink>Street Address</InputLabel>
                    <OutlinedInput
                      label="Street Address"
                      value={patientInformation.street_address}
                      onChange={handlePatientInformationChange('street_address')}
                      notched
                    />
                    {errors.patient_information_street_address && <FormHelperText>{errors.patient_information_street_address}</FormHelperText>}
                  </FormControl>

                  <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.patient_information_city}>
                    <InputLabel shrink>City</InputLabel>
                    <OutlinedInput
                      label="City"
                      value={patientInformation.city}
                      onChange={handlePatientInformationChange('city')}
                      notched
                    />
                    {errors.patient_information_city && <FormHelperText>{errors.patient_information_city}</FormHelperText>}
                  </FormControl>

                  <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.patient_information_province_state}>
                    <InputLabel shrink>Province / State</InputLabel>
                    <OutlinedInput
                      label="Province / State"
                      value={patientInformation.province_state}
                      onChange={handlePatientInformationChange('province_state')}
                      notched
                    />
                    {errors.patient_information_province_state && <FormHelperText>{errors.patient_information_province_state}</FormHelperText>}
                  </FormControl>

                  <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.patient_information_postal_zip_code}>
                    <InputLabel shrink>Postal / ZIP Code</InputLabel>
                    <OutlinedInput
                      label="Postal / ZIP Code"
                      value={patientInformation.postal_zip_code}
                      onChange={handlePatientInformationChange('postal_zip_code')}
                      notched
                    />
                    {errors.patient_information_postal_zip_code && <FormHelperText>{errors.patient_information_postal_zip_code}</FormHelperText>}
                  </FormControl>
                </Box>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Prescriptions, OTCs, and NHPs
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddPrescription}
                  disabled={isLoading}
                  sx={{ color: 'text.primary' }}
                >
                  Add Medication
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each medication added here will become its own page in the prescription overlay.
              </Typography>

              {normalizedPrescriptions.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No medication entries yet.
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {normalizedPrescriptions.map((entry, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Medication #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePrescription(index)}
                        disabled={isLoading}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_name`]}>
                          <InputLabel shrink>Medication Name</InputLabel>
                          <OutlinedInput
                            label="Medication Name"
                            value={entry.name}
                            onChange={(e) => handlePrescriptionChange(index, 'name', e.target.value)}
                            notched
                          />
                          {errors[`prescription_${index}_name`] && <FormHelperText>{errors[`prescription_${index}_name`]}</FormHelperText>}
                        </FormControl>

                        <FormControl sx={{ minWidth: 180 }} disabled={isLoading}>
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={entry.category}
                            label="Category"
                            onChange={(e) => handlePrescriptionChange(index, 'category', e.target.value)}
                          >
                            <MenuItem value="prescription">Prescription</MenuItem>
                            <MenuItem value="otc">OTC</MenuItem>
                            <MenuItem value="nhp">NHP / Supplement</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_patient_name`]}>
                          <InputLabel shrink>Patient Name</InputLabel>
                          <OutlinedInput
                            label="Patient Name"
                            value={entry.patient_name}
                            onChange={(e) => handlePrescriptionChange(index, 'patient_name', e.target.value)}
                            notched
                          />
                          {errors[`prescription_${index}_patient_name`] && <FormHelperText>{errors[`prescription_${index}_patient_name`]}</FormHelperText>}
                        </FormControl>

                        <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_written_date`]}>
                          <InputLabel shrink>Written Date</InputLabel>
                          <OutlinedInput
                            label="Written Date"
                            value={entry.written_date}
                            onChange={(e) => handlePrescriptionChange(index, 'written_date', e.target.value)}
                            notched
                          />
                          {errors[`prescription_${index}_written_date`] && <FormHelperText>{errors[`prescription_${index}_written_date`]}</FormHelperText>}
                        </FormControl>
                      </Box>

                      <FormControl fullWidth variant="outlined" disabled={isLoading}>
                        <InputLabel shrink>Dose / Strength</InputLabel>
                        <OutlinedInput
                          label="Dose / Strength"
                          value={entry.dose}
                          onChange={(e) => handlePrescriptionChange(index, 'dose', e.target.value)}
                          notched
                        />
                      </FormControl>

                      <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_directions`]}>
                        <InputLabel shrink>Directions</InputLabel>
                        <OutlinedInput
                          label="Directions"
                          multiline
                          rows={2}
                          inputProps={grammarCheckInputProps}
                          value={entry.directions}
                          onChange={(e) => handlePrescriptionChange(index, 'directions', e.target.value)}
                          notched
                        />
                        {errors[`prescription_${index}_directions`] && <FormHelperText>{errors[`prescription_${index}_directions`]}</FormHelperText>}
                      </FormControl>

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_quantity`]}>
                          <InputLabel shrink>Quantity</InputLabel>
                          <OutlinedInput
                            label="Quantity"
                            value={entry.quantity}
                            onChange={(e) => handlePrescriptionChange(index, 'quantity', e.target.value)}
                            notched
                          />
                          {errors[`prescription_${index}_quantity`] && <FormHelperText>{errors[`prescription_${index}_quantity`]}</FormHelperText>}
                        </FormControl>

                        <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_refills`]}>
                          <InputLabel shrink>Refills</InputLabel>
                          <OutlinedInput
                            label="Refills"
                            value={entry.refills}
                            onChange={(e) => handlePrescriptionChange(index, 'refills', e.target.value)}
                            notched
                          />
                          {errors[`prescription_${index}_refills`] && <FormHelperText>{errors[`prescription_${index}_refills`]}</FormHelperText>}
                        </FormControl>
                      </Box>

                      <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_prescriber`]}>
                        <InputLabel shrink>Prescriber</InputLabel>
                        <OutlinedInput
                          label="Prescriber"
                          value={entry.prescriber}
                          onChange={(e) => handlePrescriptionChange(index, 'prescriber', e.target.value)}
                          notched
                        />
                        {errors[`prescription_${index}_prescriber`] && <FormHelperText>{errors[`prescription_${index}_prescriber`]}</FormHelperText>}
                      </FormControl>

                      <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors[`prescription_${index}_signature`]}>
                        <InputLabel shrink>Signature</InputLabel>
                        <OutlinedInput
                          label="Signature"
                          multiline
                          rows={2}
                          inputProps={grammarCheckInputProps}
                          value={entry.signature}
                          onChange={(e) => handlePrescriptionChange(index, 'signature', e.target.value)}
                          notched
                        />
                        {errors[`prescription_${index}_signature`] && <FormHelperText>{errors[`prescription_${index}_signature`]}</FormHelperText>}
                      </FormControl>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Medical History
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {medicalHistorySections.map((section) => (
                  <Box key={section.key}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {section.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {section.helper}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddMedicalHistoryItem(section.key)}
                        disabled={isLoading}
                        sx={{ color: 'text.primary' }}
                      >
                        Add Item
                      </Button>
                    </Box>

                    {medicalHistory[section.key].length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        No items added yet.
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {medicalHistory[section.key].map((item, index) => (
                        <Box key={`${section.key}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            disabled={isLoading}
                            error={!!errors[`medical_history_${section.key}_${index}`]}
                          >
                            <OutlinedInput
                              value={item}
                              onChange={(e) => handleMedicalHistoryItemChange(section.key, index, e.target.value)}
                              placeholder={section.label}
                              multiline={section.key === 'social_history'}
                              minRows={section.key === 'social_history' ? 2 : undefined}
                              inputProps={grammarCheckInputProps}
                            />
                            {errors[`medical_history_${section.key}_${index}`] && (
                              <FormHelperText>{errors[`medical_history_${section.key}_${index}`]}</FormHelperText>
                            )}
                          </FormControl>

                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMedicalHistoryItem(section.key, index)}
                            disabled={isLoading}
                            sx={{ color: 'error.main', mt: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Clinical Information */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Clinical Information
              </Typography>
              <Tooltip
                title="This information directly drives the AI Patient's medical knowledge and behaviour. It defines the patient's condition, personality, and any specific instructions for how the AI should respond."
                placement="top"
                arrow
              >
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', mb: 2 }} />
              </Tooltip>
            </Box>

            <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.primary_condition}>
              <InputLabel shrink>Primary Condition</InputLabel>
              <OutlinedInput
                label="Primary Condition"
                value={formData.primary_condition || ''}
                onChange={handleChange('primary_condition')}
                notched
              />
              {errors.primary_condition
                ? <FormHelperText>{errors.primary_condition}</FormHelperText>
                : <FormHelperText><em>Shapes the AI Patient's responses and is used by the "Submit Diagnosis" feature to assess whether a student's diagnosis is correct.</em></FormHelperText>
              }
            </FormControl>

            <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.personality}>
              <InputLabel shrink>Patient Personality</InputLabel>
              <OutlinedInput
                label="Patient Personality"
                multiline
                rows={3}
                inputProps={grammarCheckInputProps}
                value={formData.personality || ''}
                onChange={handleChange('personality')}
                notched
              />
              {errors.personality && <FormHelperText>{errors.personality}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth variant="outlined" disabled={isLoading} error={!!errors.prompt_instructions}>
              <InputLabel shrink>Response Instructions for the AI Patient</InputLabel>
              <OutlinedInput
                label="Response Instructions for the AI Patient"
                multiline
                rows={3}
                inputProps={grammarCheckInputProps}
                value={formData.prompt_instructions || ''}
                onChange={handleChange('prompt_instructions')}
                notched
              />
              {errors.prompt_instructions && <FormHelperText>{errors.prompt_instructions}</FormHelperText>}
            </FormControl>

            {/* Session Settings */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Session Settings
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Avatar Idle Timeout (mins)
                </Typography>
                <Tooltip
                  title="The number of minutes the avatar session can remain idle before automatically disconnecting."
                  placement="top"
                  arrow
                >
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <FormControl
                variant="outlined"
                disabled={isLoading}
                error={!!errors.activity_idle_timeout}
                sx={{ width: 200 }}
              >
                <OutlinedInput
                  type="number"
                  value={formData.activity_idle_timeout ?? '2'}
                  onChange={handleChange('activity_idle_timeout')}
                  onBlur={() => {
                    const val = Number(formData.activity_idle_timeout);
                    if (!formData.activity_idle_timeout?.trim() || isNaN(val) || val < 1 || val > 10) {
                      setFormData(prev => ({ ...prev, activity_idle_timeout: '2' }));
                    }
                  }}
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  size="small"
                />
                {errors.activity_idle_timeout
                  ? <FormHelperText>{errors.activity_idle_timeout}</FormHelperText>
                  : <FormHelperText>1–10 minutes. Default is 2.</FormHelperText>
                }
              </FormControl>
            </Box>

            {/* Attachments Section */}
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  cursor: 'pointer'
                }}
                onClick={() => setAttachmentsExpanded(!attachmentsExpanded)}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Attachments ({attachments.length})
                </Typography>
                <IconButton size="small">
                  {attachmentsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={attachmentsExpanded}>
                <Box>
                  {/* Upload button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    onChange={handleFileSelect}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || !!uploadProgress || attachments.length >= 10}
                    sx={{ mb: 2, color: 'text.primary' }}
                  >
                    Upload File
                  </Button>

                  {attachments.length >= 10 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Maximum of 10 attachments reached.
                    </Typography>
                  )}

                  {uploadProgress && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {uploadProgress}
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {isLoadingAttachments && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload PDFs, images, or documents (max 10MB each).
                  </Typography>

                  {attachments.length > 0 && (
                    <List dense>
                      {attachments.map((att) => (
                        <ListItem
                          key={att.id}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleDeleteAttachment(att.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>{getAttachmentIcon(att.mimeType)}</ListItemIcon>
                          <ListItemText
                            primary={
                              <a
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'none', color: 'inherit' }}
                                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                              >
                                {att.fileName}
                              </a>
                            }
                            secondary={formatFileSize(att.fileSize)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Collapse>
            </Box>

            {/* Enhanced Scoring Section */}
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  cursor: 'pointer'
                }}
                onClick={() => setScoringExpanded(!scoringExpanded)}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Scoring Rubric ({scoringItems.length} items)
                </Typography>
                <IconButton size="small">
                  {scoringExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={scoringExpanded}>
                <Box>
                  {hasAnyScoringErrors && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Please fix the errors in the scoring items below.
                    </Alert>
                  )}

                  {scoringItems.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No scoring items found. Add items to create a scoring rubric.
                    </Typography>
                  )}

                  {scoringItems.map((item, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 3,
                        mb: 2,
                        backgroundColor: 'background.paper',
                        border: hasAnyScoringErrors && scoringErrors[index] ? '1px solid' : '1px solid',
                        borderColor: hasAnyScoringErrors && scoringErrors[index] ? 'error.main' : 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          Scoring Item #{index + 1}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveScoringItem(index, index - 1)}
                            disabled={isLoading || index === 0}
                            sx={{ color: 'text.secondary' }}
                            title="Move up"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveScoringItem(index, index + 1)}
                            disabled={isLoading || index === scoringItems.length - 1}
                            sx={{ color: 'text.secondary' }}
                            title="Move down"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveScoringItem(index)}
                            sx={{ color: 'error.main' }}
                            disabled={isLoading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl 
                          fullWidth 
                          variant="outlined" 
                          disabled={isLoading} 
                          error={!!(scoringErrors[index]?.scoringCategory)}
                        >
                          <InputLabel shrink>Category Name *</InputLabel>
                          <OutlinedInput
                            label="Category Name *"
                            value={item.scoringCategory}
                            onChange={(e) =>
                              handleScoringItemChange(index, 'scoringCategory', e.target.value)
                            }
                            placeholder="e.g., Introduced self and role"
                            notched
                    />
                          {scoringErrors[index]?.scoringCategory && (
                            <FormHelperText>{scoringErrors[index].scoringCategory}</FormHelperText>
                          )}
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <FormControl
                            variant="outlined"
                            disabled={isLoading}
                            error={!!(scoringErrors[index]?.totalPoints)}
                            sx={{ width: 200 }}
                          >
                            <InputLabel shrink>Total Points *</InputLabel>
                            <OutlinedInput
                              type="number"
                              value={item.totalPoints}
                              onChange={(e) =>
                                handleScoringItemChange(index, 'totalPoints', e.target.value)
                              }
                              inputProps={{ min: 1, max: 100 }}
                              label="Total Points *"
                              notched
                            />
                            {scoringErrors[index]?.totalPoints && (
                              <FormHelperText>{scoringErrors[index].totalPoints}</FormHelperText>
                            )}
                          </FormControl>

                          <FormControl
                            disabled={isLoading}
                            sx={{ minWidth: 180 }}
                          >
                            <InputLabel>Evaluation Timing</InputLabel>
                            <Select
                              value={item.evaluationTiming || 'per-exchange'}
                              label="Evaluation Timing"
                              onChange={(e) =>
                                handleScoringItemChange(index, 'evaluationTiming', e.target.value)
                              }
                              size="small"
                            >
                              <MenuItem value="per-exchange">Per Exchange</MenuItem>
                              <MenuItem value="end-of-session">End of Session</MenuItem>
                            </Select>
                            <FormHelperText sx={{ color: 'text.secondary' }}>
                              {(item.evaluationTiming || 'per-exchange') === 'end-of-session'
                                ? 'Scored once at diagnosis submission'
                                : 'Scored after each exchange'}
                            </FormHelperText>
                          </FormControl>
                        </Box>

                        <FormControl 
                          fullWidth 
                          variant="outlined" 
                          disabled={isLoading}
                          error={!!(scoringErrors[index]?.categorySpecificInstructions)}
                        >
                          <InputLabel shrink>Category Evaluation Instructions</InputLabel>
                          <OutlinedInput
                            label="Category Evaluation Instructions"
                            multiline
                            rows={3}
                            inputProps={grammarCheckInputProps}
                            value={item.categorySpecificInstructions || ''}
                            onChange={(e) =>
                              handleScoringItemChange(index, 'categorySpecificInstructions', e.target.value)
                            }
                            placeholder="Enter evaluation instructions separated by semicolons (;)"
                            notched
                          />
                          <FormHelperText sx={{ color: scoringErrors[index]?.categorySpecificInstructions ? 'error.main' : 'text.secondary' }}>
                            {scoringErrors[index]?.categorySpecificInstructions || 
                             'Separate instructions using semicolons. Example - 1 point for prescription medications; 1 point for over-the-counter medications; 1 point for natural health products or vitamins'}
                          </FormHelperText>
                        </FormControl>
                      </Box>
                    </Paper>
                  ))}

                  <Button
                    variant="outlined"
                    onClick={handleAddScoringItem}
                    disabled={isLoading}
                    sx={{ mt: 1, color: 'text.primary' }}
                  >
                    Add Scoring Item
                  </Button>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={isLoading}
            sx={{ 
              color: '#FFFFFF',
              textTransform: 'none',
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.main',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: 'primary.main',
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Update Case'
            )}
          </Button>
        </DialogActions>
      </form>
      )}
    </Dialog>
    <Dialog
      open={isUnsavedWarningOpen}
      onClose={handleKeepEditing}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
        }}
      >
        <WarningIcon sx={{ color: 'warning.main' }} />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Unsaved Changes
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          You have unsaved changes to this case. If you close the editor now, those changes will be lost.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleKeepEditing}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
          }}
        >
          Keep Editing
        </Button>
        <Button
          onClick={handleDiscardChanges}
          variant="contained"
          sx={{
            backgroundColor: 'warning.main',
            color: 'text.primary',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'warning.dark',
            },
          }}
        >
          Discard Changes
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};
